// ============================================================
// manual.js — la marca «a mano» de los colores de un bloque
// (plan de personalización manual, 16-10-2026, §3.1; decisiones D1, D4 y D5 del dueño)
//
// Módulo PURO y BASE: no importa nada (lo importa `contrast.js`, que carga cada bloque, también en
// el sitio publicado) y no toca DOM ni `window`. ES2019: sin `?.` ni `??`.
//
// QUÉ ES. `props.manual` dice qué colores del bloque ha elegido el cliente A MANO (fila
// «Personalizado» de Color de la sección). Lo que lleva esa marca:
//   · `safe()` (contrast.js) NO lo corrige al pintar: lo pintado es lo elegido (D1: «se respeta lo
//     elegido»; el aviso de contraste lo da el panel, nunca una corrección en silencio);
//   · `applyPaletteToBlocks` (palettes.js) no lo pisa al re-paletizar (D4);
//   · la varita y las variaciones (`ai-merge.js`) lo REPONEN salvo que la orden hable de color, y
//     entonces gana la IA y la clave sale de la marca (D5, decidido en CPU con `orden-color.js`).
//
// FORMA: un objeto `{clave: valorAutomático}`, no una lista. La clave es el prop elegido a mano
// (`bgColor`, `textColor`) y el valor, el color AUTOMÁTICO que había debajo (el que tenía el bloque
// al marcarlo, y el que le habría puesto después la paleta). Es lo que permite «Restablecer» y
// «Automático» sin adivinar: quitar la marca devuelve ese valor. Vacío (`''`) = no había ninguno.
// Sin `_` delante: `limpiar_json_basura` (views.py) borra las claves `_*` al guardar.
//
// FRONTERA: el servidor valida la forma al guardar (`views.py::_sanitizar_props_css`, con su espejo
// `_CLAVES_MANUALES`: claves de esta lista y valores hex o vacíos). Los valores vuelven a `bgColor`/
// `textColor`, que se interpolan en el `style=` del <section> (gotcha 35c): por eso solo hex.
// ============================================================

/**
 * Props que admiten la marca. Solo los que la paleta o la IA reescriben y el panel expone hoy (lo
 * nuevo de las fases siguientes —botón, tamaños, fuente— no está en el esquema del LLM ni lo toca
 * la paleta: sobrevive sin marca). Ampliarla = también `_CLAVES_MANUALES` en views.py (lo compara
 * `tests/test_manual_respetado.py`).
 */
export const CLAVES_MANUALES = ['bgColor', 'textColor'];

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** ¿Hex literal (#rgb / #rrggbb)? */
export function esHexColor(v) {
    return typeof v === 'string' && HEX_RE.test(v.trim());
}

function esObjetoPlano(v) {
    return !!v && typeof v === 'object' && !Array.isArray(v);
}

/**
 * La marca del bloque, NORMALIZADA (copia): solo claves de CLAVES_MANUALES y valores hex o ''.
 * Una marca rota (lista, cadena, valores raros) no rompe nada: lo que no se entiende no cuenta.
 * @returns {Object<string,string>}
 */
export function manualDe(props) {
    const out = {};
    const m = props && props.manual;
    if (!esObjetoPlano(m)) return out;
    CLAVES_MANUALES.forEach(k => {
        if (!Object.prototype.hasOwnProperty.call(m, k)) return;
        out[k] = esHexColor(m[k]) ? m[k].trim() : '';
    });
    return out;
}

/** ¿El cliente eligió `clave` a mano? Exige además que el prop exista: una marca sin valor no vale. */
export function esManual(props, clave) {
    if (!props || CLAVES_MANUALES.indexOf(clave) === -1) return false;
    const v = props[clave];
    return typeof v === 'string' && v.trim() !== ''
        && Object.prototype.hasOwnProperty.call(manualDe(props), clave);
}

/**
 * La marca que resulta de marcar unas claves y quitar otras. Devuelve el objeto nuevo o `null` si
 * se queda vacía (en un parche, `null` BORRA el prop: así un bloque sin nada a mano no lleva la
 * clave y sale byte a byte como antes).
 *
 * @param {Object} props   props ACTUALES del bloque (de ahí sale el valor automático al marcar).
 * @param {string[]} [marcar] claves que pasan a mano. Si ya lo estaban, conservan su valor
 *                         automático de entonces (si no, «Restablecer» devolvería el elegido).
 * @param {string[]} [quitar] claves que vuelven a ser automáticas.
 */
export function marcaCon(props, marcar, quitar) {
    const m = manualDe(props);
    (marcar || []).forEach(k => {
        if (CLAVES_MANUALES.indexOf(k) === -1 || Object.prototype.hasOwnProperty.call(m, k)) return;
        const actual = props ? props[k] : '';
        m[k] = esHexColor(actual) ? actual.trim() : '';
    });
    (quitar || []).forEach(k => { delete m[k]; });
    return Object.keys(m).length ? m : null;
}
